/**
 * Unified Logger - works on both client and server
 *
 * Auto-detects environment and uses appropriate implementation:
 * - Server (Node.js): JSON logging (stdout/stderr) or ANSI-formatted console
 * - Client (Browser): Console with CSS-styled output
 *
 * @example
 * ```typescript
 * import { createLogger } from '@maestro/core/logger'
 *
 * const log = createLogger('maestro:myservice')
 * log.info('Something happened', { data })
 * ```
 */

import {
  ANSI_COLORS,
  CLIENT_LEVEL_COLORS,
  LEVEL_COLORS,
  type LogLevel,
} from '@maestro/core/logger/constants'
import {
  isSerializedError,
  sanitizeLogs,
  sanitizeString,
  type SerializedError,
} from '@maestro/core/logger/log-sanitizer'
import {
  formatTimestamp,
  getNamespaceWidth,
  safeStringify,
  shouldLog,
  truncateNamespace,
} from '@maestro/core/logger/utils'

// =============================================================================
// Namespace matching (duplicated here to avoid server-only dependency)
// =============================================================================

/**
 * Match namespace against a glob-like pattern.
 * Supports:
 *   - `*` - match all namespaces
 *   - `prefix:*` - match prefix and all children (any depth)
 *   - `*:suffix` - match anything ending with :suffix
 *   - `prefix:*:suffix` - match prefix, any middle segments, and suffix
 *   - `**` - same as * (match anything including colons)
 *   - `prefix` - exact match OR parent (matches prefix:child:grandchild)
 */
function namespaceMatches(namespace: string, pattern: string): boolean {
  if (pattern === '*') {
    return true
  }
  if (pattern === namespace) {
    return true
  }

  // Check if pattern contains wildcard
  if (pattern.includes('*')) {
    // Convert glob pattern to regex
    // * matches any characters (including colons) - greedy multi-segment match
    const regexStr = pattern
      .replace(/[.+?^${}()|[\]\\]/g, '\\$&') // Escape special regex chars
      .replace(/\*+/g, '.*') // * or ** matches anything

    // For :* at end, also match the parent itself (without children)
    if (pattern.endsWith(':*')) {
      const prefix = pattern.slice(0, -2)
      const prefixEscaped = prefix
        .replace(/[.+?^${}()|[\]\\]/g, '\\$&')
        .replace(/\*+/g, '.*')
      const regex = new RegExp(`^${prefixEscaped}(:.+)?$`)
      return regex.test(namespace)
    }

    const regex = new RegExp(`^${regexStr}$`)
    return regex.test(namespace)
  }

  // Parent pattern matches child namespaces
  // e.g., "maestro:sap" matches "maestro:sap:catalog"
  if (namespace.startsWith(`${pattern}:`)) {
    return true
  }

  return false
}

function isNamespaceEnabled(
  namespace: string,
  enabled: string[],
  disabled: string[]
): boolean {
  for (const pattern of disabled) {
    if (namespaceMatches(namespace, pattern)) {
      return false
    }
  }
  for (const pattern of enabled) {
    if (namespaceMatches(namespace, pattern)) {
      return true
    }
  }
  return false
}

// Re-export LogLevel for consumers
export type { LogLevel }

// Client only has 4 levels (http/verbose/silly map to debug)
type ClientLogLevel = 'error' | 'warn' | 'info' | 'debug'

export interface Logger {
  error(message: string, meta?: unknown): void
  warn(message: string, meta?: unknown): void
  info(message: string, meta?: unknown): void
  http(message: string, meta?: unknown): void
  verbose(message: string, meta?: unknown): void
  debug(message: string, meta?: unknown): void
  silly(message: string, meta?: unknown): void
  child(meta: Record<string, unknown>): Logger
  readonly namespace: string
  readonly isEnabled: boolean
  readonly level: LogLevel
}

function getNamespaceLevel(
  namespace: string,
  namespaceLevels: Record<string, LogLevel>,
  defaultLevel: LogLevel
): LogLevel {
  const matchingPatterns = Object.keys(namespaceLevels).filter((p) =>
    namespaceMatches(namespace, p)
  )
  if (matchingPatterns.length === 0) {
    return defaultLevel
  }

  matchingPatterns.sort((a, b) => {
    if (a === namespace) {
      return -1
    }
    if (b === namespace) {
      return 1
    }
    return b.length - a.length
  })

  return namespaceLevels[matchingPatterns[0]]
}

// =============================================================================
// Environment detection
// =============================================================================

const isServer = typeof window === 'undefined'
const isBuildPhase =
  typeof process !== 'undefined' &&
  process.env?.NEXT_PHASE === 'phase-production-build'
const isProduction =
  typeof process !== 'undefined' && process.env?.NODE_ENV === 'production'

/**
 * Check if ANSI colors should be disabled.
 * Useful for Firefox which doesn't render ANSI in browser console for server logs.
 * Set LOG_NO_ANSI=true to disable colors.
 */
const noAnsiColors =
  typeof process !== 'undefined' &&
  (process.env?.LOG_NO_ANSI === 'true' || process.env?.LOG_NO_ANSI === '1')

/**
 * Check if expandable objects mode should be enabled.
 * When enabled, objects are passed directly to console methods
 * so they appear as expandable/inspectable in debug consoles.
 *
 * Auto-detects debugger via:
 * - inspector.url() - most reliable runtime check
 * - --inspect flags in execArgv or NODE_OPTIONS
 * - v8debug global (legacy)
 *
 * Can be manually controlled via LOG_EXPANDABLE_OBJECTS=true/false
 */
function isExpandableObjectsEnabled(): boolean {
  if (typeof process === 'undefined') {
    return false
  }
  // Manual override via environment variable
  const envOverride = process.env?.LOG_EXPANDABLE_OBJECTS
  if (envOverride === 'true' || envOverride === '1') {
    return true
  }
  if (envOverride === 'false' || envOverride === '0') {
    return false
  }

  // Check if debugger is attached using inspector.url()
  // This is the most reliable runtime check for VS Code and other debuggers
  try {
    // Use eval to hide from bundlers (inspector is Node.js only)
    const inspector = eval("require('inspector')")
    if (typeof inspector?.url === 'function' && inspector.url()) {
      return true
    }
  } catch {
    // Inspector not available (browser, edge runtime, etc.)
  }

  // Fallback checks for when inspector module isn't available
  // Check for --inspect or --inspect-brk flags in execArgv
  const execArgv = process.execArgv?.join(' ') || ''
  if (/--inspect/.test(execArgv)) {
    return true
  }
  // Check NODE_OPTIONS env var (used by VS Code debugger)
  const nodeOptions = process.env?.NODE_OPTIONS || ''
  if (/--inspect/.test(nodeOptions)) {
    return true
  }
  // Check for v8 debugger API (legacy attached debugger)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (typeof (globalThis as any).v8debug === 'object') {
    return true
  }
  return false
}

// Cache the check at module load
const expandableObjectsEnabled = isExpandableObjectsEnabled()

/**
 * Check if JSON logging is enabled.
 * Only enabled on server-side. Defaults to true in production, false otherwise.
 */
function isJsonLoggingEnabled(): boolean {
  // Never enable JSON logging on the client (browser)
  if (!isServer) {
    return false
  }
  if (typeof process === 'undefined') {
    return false
  }
  const env = process.env?.ENABLE_JSON_LOGGING
  if (env === 'true' || env === '1') {
    return true
  }
  if (env === 'false' || env === '0') {
    return false
  }
  return isProduction
}

// Cache JSON logging check at module load
const jsonLoggingEnabled = isJsonLoggingEnabled()

/**
 * Write a JSON log entry directly to stdout/stderr, bypassing console interception.
 * Used when JSON logging is enabled but Winston is unavailable (edge runtime).
 *
 * Output structure (Option B):
 * - Fixed fields: level, namespace, message, ts
 * - error: First-class Error object (when present)
 * - meta: All other contextual data
 */
function writeJsonLog(
  level: LogLevel,
  namespace: string,
  message: string,
  meta: Record<string, unknown>
): void {
  const output: Record<string, unknown> = {
    level,
    namespace,
    message,
    ts: new Date().toISOString(),
  }

  // The first (already serialized) error becomes the top-level 'error' field
  let errorObj: SerializedError | undefined
  const remainingMeta: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(meta)) {
    if (!errorObj && isSerializedError(value)) {
      errorObj = value
    } else {
      remainingMeta[key] = value
    }
  }

  if (errorObj) {
    output.error = errorObj
  }

  if (Object.keys(remainingMeta).length > 0) {
    output.meta = remainingMeta
  }

  const line = safeStringify(output) + '\n'
  if (level === 'error' || level === 'warn') {
    process.stderr.write(line)
  } else {
    process.stdout.write(line)
  }
}

/**
 * Normalize meta to an object (primitives become `{ value }`, an Error becomes
 * `{ error }`), merge the child logger's base meta and sanitize message and meta.
 */
function sanitizeLogCall(
  message: string,
  meta: unknown,
  baseMeta: Record<string, unknown>
): { sanitizedMessage: string; combinedMeta: Record<string, unknown> } {
  const metaObj =
    meta instanceof Error
      ? { error: meta }
      : meta != null && typeof meta === 'object' && !Array.isArray(meta)
        ? (meta as Record<string, unknown>)
        : meta !== undefined
          ? { value: meta }
          : {}
  return {
    sanitizedMessage: sanitizeString(message),
    combinedMeta: sanitizeLogs([{ ...baseMeta, ...metaObj }])[0] as Record<
      string,
      unknown
    >,
  }
}

// =============================================================================
// Configuration parsing
// =============================================================================

interface ParsedConfig {
  enabled: string[]
  disabled: string[]
  namespaceLevels: Record<string, LogLevel>
  defaultLevel: LogLevel
}

function parseConfig(): ParsedConfig {
  const validLevels = [
    'error',
    'warn',
    'info',
    'http',
    'verbose',
    'debug',
    'silly',
  ]

  // Server: use process.env, Client: use localStorage
  let rawNamespaces: string | null = null
  let rawLevels: string | null = null
  let rawDefaultLevel: string | null = null

  if (isServer) {
    rawNamespaces = process.env.LOG_NAMESPACES || null
    rawLevels = process.env.LOG_LEVELS || null
    rawDefaultLevel = process.env.LOG_LEVEL || null
  } else {
    try {
      rawNamespaces = localStorage.getItem('LOG_NAMESPACES')
      rawLevels = localStorage.getItem('LOG_LEVELS')
      rawDefaultLevel = localStorage.getItem('LOG_LEVEL')
    } catch {
      // localStorage not available
    }
  }

  // Parse namespaces
  let enabled: string[] = ['*']
  const disabled: string[] = []

  if (rawNamespaces && rawNamespaces !== '*') {
    const parts = rawNamespaces
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
    enabled = []
    for (const part of parts) {
      if (part.startsWith('-')) {
        disabled.push(part.slice(1))
      } else {
        enabled.push(part)
      }
    }
    if (enabled.length === 0 && disabled.length > 0) {
      enabled.push('*')
    }
  }

  // Parse namespace levels
  const namespaceLevels: Record<string, LogLevel> = {}
  if (rawLevels) {
    const parts = rawLevels
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
    for (const part of parts) {
      const eqIndex = part.indexOf('=')
      if (eqIndex === -1) {
        continue
      }
      const ns = part.slice(0, eqIndex).trim()
      const lvl = part.slice(eqIndex + 1).trim()
      if (ns && validLevels.includes(lvl)) {
        namespaceLevels[ns] = lvl as LogLevel
      }
    }
  }

  // Default level
  let defaultLevel: LogLevel = 'info'
  if (rawDefaultLevel && validLevels.includes(rawDefaultLevel)) {
    defaultLevel = rawDefaultLevel as LogLevel
  } else if (isServer) {
    defaultLevel = isProduction ? 'info' : 'debug'
  } else {
    // Client: warn in prod, info in dev
    defaultLevel = isProduction ? 'warn' : 'info'
  }

  return { enabled, disabled, namespaceLevels, defaultLevel }
}

// Parse config once at module load
const config = parseConfig()

// =============================================================================
// Formatting utilities
// =============================================================================

const MAX_NS_WIDTH = getNamespaceWidth()

// =============================================================================
// Client Logger Implementation (CSS-styled console)
// =============================================================================

function createClientLoggerImpl(
  namespace: string,
  baseMeta: Record<string, unknown> = {}
): Logger {
  const loggerEnabled = isNamespaceEnabled(
    namespace,
    config.enabled,
    config.disabled
  )
  const level = getNamespaceLevel(
    namespace,
    config.namespaceLevels,
    config.defaultLevel
  )

  const log = (logLevel: LogLevel, message: string, meta?: unknown): void => {
    try {
      if (!loggerEnabled || !shouldLog(logLevel, level)) {
        return
      }

      // Map extended levels to client levels
      const clientLevel: ClientLogLevel =
        logLevel === 'http' || logLevel === 'verbose' || logLevel === 'silly'
          ? 'debug'
          : (logLevel as ClientLogLevel)

      const time = formatTimestamp()
      const truncatedNs = truncateNamespace(namespace, MAX_NS_WIDTH)
      const { sanitizedMessage, combinedMeta } = sanitizeLogCall(
        message,
        meta,
        baseMeta
      )

      const format = `%c${time} ${truncatedNs}%c:%c ${sanitizedMessage}`
      const styles = [
        'color: #6b7280',
        CLIENT_LEVEL_COLORS[clientLevel],
        'color: inherit',
      ]

      const args =
        Object.keys(combinedMeta).length > 0
          ? [format, ...styles, combinedMeta]
          : [format, ...styles]

      switch (clientLevel) {
        case 'error':
          console.error(...args)
          break
        case 'warn':
          console.warn(...args)
          break
        case 'info':
          console.info(...args)
          break
        case 'debug':
          console.debug(...args)
          break
      }
    } catch {
      // Logger must never crash the application
    }
  }

  const logger: Logger = {
    error: (msg, meta) => log('error', msg, meta),
    warn: (msg, meta) => log('warn', msg, meta),
    info: (msg, meta) => log('info', msg, meta),
    http: (msg, meta) => log('http', msg, meta),
    verbose: (msg, meta) => log('verbose', msg, meta),
    debug: (msg, meta) => log('debug', msg, meta),
    silly: (msg, meta) => log('silly', msg, meta),
    child: (childMeta) =>
      createClientLoggerImpl(namespace, { ...baseMeta, ...childMeta }),
    namespace,
    isEnabled: loggerEnabled,
    level,
  }

  return logger
}

// =============================================================================
// Server Logger Implementation
// =============================================================================

function createServerLoggerImpl(
  namespace: string,
  baseMeta: Record<string, unknown> = {}
): Logger {
  const loggerEnabled = isNamespaceEnabled(
    namespace,
    config.enabled,
    config.disabled
  )
  const level = getNamespaceLevel(
    namespace,
    config.namespaceLevels,
    config.defaultLevel
  )

  const log = (logLevel: LogLevel, message: string, meta?: unknown): void => {
    try {
      if (isBuildPhase) {
        return
      }
      if (!loggerEnabled || !shouldLog(logLevel, level)) {
        return
      }

      const { sanitizedMessage, combinedMeta } = sanitizeLogCall(
        message,
        meta,
        baseMeta
      )

      // JSON logging: write directly to stdout/stderr to bypass console interception
      if (jsonLoggingEnabled && !expandableObjectsEnabled) {
        writeJsonLog(logLevel, namespace, sanitizedMessage, combinedMeta)
        return
      }

      // Console output with ANSI colors (dev mode, edge runtime, debugger attached)
      const time = formatTimestamp()
      const truncatedNs = truncateNamespace(namespace, MAX_NS_WIDTH)
      const levelColor = LEVEL_COLORS[logLevel]
      const hasMeta = Object.keys(combinedMeta).length > 0

      // When expandable objects mode is enabled, pass objects directly for inspection
      // in debug consoles. Source location is handled via VS Code's skipFiles in launch.json.
      if (expandableObjectsEnabled) {
        // Use ANSI colors unless LOG_NO_ANSI is set (useful for Firefox which doesn't render ANSI)
        const prefix = noAnsiColors
          ? `${time} ${truncatedNs}: ${sanitizedMessage}`
          : `${levelColor}${time}${ANSI_COLORS.reset} ${ANSI_COLORS.dim}${truncatedNs}${levelColor}:${ANSI_COLORS.reset} ${sanitizedMessage}`
        if (hasMeta) {
          switch (logLevel) {
            case 'error':
              console.error(prefix, combinedMeta)
              break
            case 'warn':
              console.warn(prefix, combinedMeta)
              break
            default:
              console.log(prefix, combinedMeta)
          }
        } else {
          switch (logLevel) {
            case 'error':
              console.error(prefix)
              break
            case 'warn':
              console.warn(prefix)
              break
            default:
              console.log(prefix)
          }
        }
      } else {
        // Standard formatted output — write directly to stdout/stderr
        // to avoid being re-captured by the console interceptor
        const metaStr = hasMeta
          ? noAnsiColors
            ? ` ${safeStringify(combinedMeta)}`
            : ` ${ANSI_COLORS.dim}${safeStringify(combinedMeta)}${ANSI_COLORS.reset}`
          : ''
        const formatted = noAnsiColors
          ? `${time} ${truncatedNs}: ${sanitizedMessage}${metaStr}`
          : `${levelColor}${time}${ANSI_COLORS.reset} ${ANSI_COLORS.dim}${truncatedNs}${levelColor}:${ANSI_COLORS.reset} ${sanitizedMessage}${metaStr}`

        const line = formatted + '\n'
        if (logLevel === 'error' || logLevel === 'warn') {
          process.stderr.write(line)
        } else {
          process.stdout.write(line)
        }
      }
    } catch {
      // Logger must never crash the application
    }
  }

  const logger: Logger = {
    error: (msg, meta) => log('error', msg, meta),
    warn: (msg, meta) => log('warn', msg, meta),
    info: (msg, meta) => log('info', msg, meta),
    http: (msg, meta) => log('http', msg, meta),
    verbose: (msg, meta) => log('verbose', msg, meta),
    debug: (msg, meta) => log('debug', msg, meta),
    silly: (msg, meta) => log('silly', msg, meta),
    child: (childMeta) =>
      createServerLoggerImpl(namespace, { ...baseMeta, ...childMeta }),
    namespace,
    isEnabled: loggerEnabled,
    level,
  }

  return logger
}

// =============================================================================
// Unified createLogger
// =============================================================================

/**
 * Create a namespaced logger that works in both client and server environments
 *
 * @param namespace - The namespace for this logger (e.g., 'maestro:sap:auth')
 * @returns A Logger instance
 *
 * @example
 * ```typescript
 * import { createLogger } from '@maestro/core/logger'
 *
 * const log = createLogger('maestro:myservice')
 *
 * log.info('Operation started', { operationId: '123' })
 * log.debug('Processing data')
 * log.error('Operation failed', { error: err.message })
 *
 * // Create child with additional context
 * const requestLog = log.child({ requestId: req.id })
 * requestLog.info('Processing request')
 * ```
 *
 * Configuration (Server - env vars):
 * - LOG_NAMESPACES="maestro:*,-maestro:verbose:*" (enable/disable patterns)
 * - LOG_LEVELS="maestro:auth=debug,maestro:api=warn" (per-namespace levels)
 * - LOG_LEVEL="info" (default level)
 *
 * Configuration (Client - localStorage):
 * - localStorage.setItem('LOG_NAMESPACES', 'maestro:*')
 * - localStorage.setItem('LOG_LEVEL', 'debug')
 */
export function createLogger(namespace: string): Logger {
  if (isServer) {
    return createServerLoggerImpl(namespace)
  }
  return createClientLoggerImpl(namespace)
}

// Legacy exports for backwards compatibility
export type { Logger as NamespacedLogger }
export type { Logger as ClientLogger }

/**
 * @deprecated Use createLogger instead
 */
export function createClientLogger(namespace: string): Logger {
  return createLogger(namespace)
}
